"use client";

import { useState, useCallback, useRef, useId } from "react";
import {
  Wallet,
  ArrowRight,
  CheckCircle2,
  Loader2,
  ExternalLink,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import {
  StellarWalletsKit,
  WalletNetwork,
  allowAllModules,
  XBULL_ID,
} from "@creit.tech/stellar-wallets-kit";
import {
  TransactionBuilder,
  Networks,
  Contract,
  Address,
  nativeToScVal,
  rpc,
} from "@stellar/stellar-sdk";
import { Horizon } from "@stellar/stellar-sdk/minimal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useWallet } from "@/contexts/WalletContext";
import {
  TESTNET_TOKENS,
  TOKEN_UNIT,
  HOOPS_NETWORK_PASSPHRASE,
  HOOPS_RPC_URL,
  getTxExplorerUrl,
} from "@/lib/hoops-contracts";
import type {
  BalanceLine,
  BalanceLineNative,
  BalanceLineAsset,
  BalanceLineLiquidityPool,
  AccountResponse,
} from "@/utils/types";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const HORIZON_URL = "https://horizon-testnet.stellar.org";
const NETWORK_PASSPHRASE = HOOPS_NETWORK_PASSPHRASE;

// USDC issuer on Stellar testnet (the classic issuer, not the SAC)
// This is needed to find the classic wallet's USDC trustline balance.
const USDC_ISSUER = "GBBD47IF6LWK7P7MDEVSCWR7DPUWV3NY3DTQEVFL4NAT4AQH3ZLLFLA5";
const USDC_CODE = "USDC";

// Soroban fee budget for a SAC transfer invocation
const SOROBAN_FEE = "1000000"; // 0.1 XLM, generous for simulation fallback

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Asset = "XLM" | "USDC";

type TxPhase = "idle" | "building" | "signing" | "submitting" | "success" | "error";

interface WalletFundProps {
  /** The C... Soroban contract address of the smart account to fund */
  smartAccountAddress: string;
  onSuccess?: () => void;
}

interface ClassicBalance {
  xlm: string;
  usdc: string | null; // null = no trustline / not found
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function truncateAddress(addr: string): string {
  if (addr.length <= 12) return addr;
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

/** Parse a human-readable amount string into stroops (bigint, 7 decimals). */
function parseToStroops(value: string): bigint | null {
  const parsed = parseFloat(value);
  if (!isFinite(parsed) || parsed <= 0) return null;
  // Use string math to avoid floating point precision issues
  const rounded = Math.round(parsed * TOKEN_UNIT);
  return BigInt(rounded);
}

/** Format stroops to a human-readable string with up to 7 decimal places. */
function formatStroops(stroops: bigint): string {
  const whole = stroops / BigInt(TOKEN_UNIT);
  const frac = (stroops % BigInt(TOKEN_UNIT)).toString().padStart(7, "0").replace(/0+$/, "");
  return frac.length > 0 ? `${whole.toString()}.${frac}` : whole.toString();
}

/** Format a Horizon balance string, trimming trailing zeros. */
function formatHorizonBalance(balance: string): string {
  const num = parseFloat(balance);
  if (!isFinite(num)) return "0";
  // Show up to 4 decimals, strip trailing zeros
  const formatted = num.toFixed(4).replace(/\.?0+$/, "");
  return formatted;
}

// ---------------------------------------------------------------------------
// Build the wallet's sign function — mirrors ConnectWallet.tsx exactly
// ---------------------------------------------------------------------------

function buildSigner(
  kit: StellarWalletsKit,
): (txXDR: string, networkPassphrase: string) => Promise<string> {
  return async (txXDR: string, networkPassphrase: string): Promise<string> => {
    const kitAny = kit as unknown as {
      signTransaction: (...args: unknown[]) => Promise<
        string | { signedXDR?: string; signedTxXdr?: string }
      >;
    };
    const network =
      String(networkPassphrase) === String(Networks.PUBLIC)
        ? WalletNetwork.PUBLIC
        : WalletNetwork.TESTNET;

    try {
      const result = await kitAny.signTransaction(txXDR, { network });

      if (typeof result === "string") return result;

      if (typeof result === "object" && result !== null) {  
        if ("signedTxXdr" in result) {
          const maybe = (result as { signedTxXdr?: string }).signedTxXdr;
          if (maybe) return maybe;
        }
        if ("signedXDR" in result) {
          const maybe = (result as { signedXDR?: string }).signedXDR;
          if (maybe) return maybe;
        }
      }
    } catch {
      const fallback = await kitAny.signTransaction(txXDR, network);
      if (typeof fallback === "string") return fallback;
      if (typeof fallback === "object" && fallback !== null) {  
        if ("signedTxXdr" in fallback) {
          const maybe = (fallback as { signedTxXdr?: string }).signedTxXdr;
          if (maybe) return maybe;
        }
        if ("signedXDR" in fallback) {
          const maybe = (fallback as { signedXDR?: string }).signedXDR;
          if (maybe) return maybe;
        }
      }
    }

    throw new Error("Wallet did not return a signed transaction.");
  };
}

// ---------------------------------------------------------------------------
// Fetch classic wallet balances from Horizon
// ---------------------------------------------------------------------------

async function fetchClassicBalances(address: string): Promise<ClassicBalance> {
  const server = new Horizon.Server(HORIZON_URL, { allowHttp: false });
  const account: AccountResponse = await server.loadAccount(address);
  const balances: BalanceLine[] = account.balances;

  let xlm = "0";
  let usdc: string | null = null;

  for (const b of balances) {
    if (b.asset_type === "native") {
      xlm = (b).balance;
    } else if (b.asset_type === "credit_alphanum4" || b.asset_type === "credit_alphanum12") {
      const assetBalance = b;
      if (
        assetBalance.asset_code === USDC_CODE &&
        assetBalance.asset_issuer === USDC_ISSUER
      ) {
        usdc = assetBalance.balance;
      }
    }
  }

  return { xlm, usdc };
}

// ---------------------------------------------------------------------------
// Build a Soroban invocation TX that calls SAC.transfer(from, to, amount)
//
// Classic `payment` ops cannot target contract addresses (C...).
// The SAC (Stellar Asset Contract) wraps the native asset / classic token and
// exposes a Soroban `transfer(from, to, amount: i128)` function.
// The classic wallet signs the TX with its ed25519 key — auth is satisfied
// because `from` == the classic wallet's address.
//
// We simulate via Soroban RPC first to obtain the ledger footprint and
// resource fee, then apply them with rpc.assembleTransaction — the same
// pattern used throughout hoops-passkey-kit.ts.
// ---------------------------------------------------------------------------

async function buildSacTransferTx(
  sourceAddress: string,
  sacAddress: string,
  destinationAddress: string,
  amountStroops: bigint,
): Promise<string> {
  const horizonServer = new Horizon.Server(HORIZON_URL, { allowHttp: false });
  const rpcServer = new rpc.Server(HOOPS_RPC_URL);

  const accountResponse = await horizonServer.loadAccount(sourceAddress);
  // Wrap Horizon response into an Account object compatible with TransactionBuilder
  const { Account: StellarAccount } = await import("@stellar/stellar-sdk");
  const account = new StellarAccount(accountResponse.accountId(), accountResponse.sequenceNumber());

  const sacContract = new Contract(sacAddress);
  const fromScVal = new Address(sourceAddress).toScVal();
  const toScVal = new Address(destinationAddress).toScVal();
  const amountScVal = nativeToScVal(amountStroops, { type: "i128" });

  const transferOp = sacContract.call("transfer", fromScVal, toScVal, amountScVal);

  // Build a draft TX for simulation — fee will be replaced after simulation
  const draftTx = new TransactionBuilder(account, {
    fee: SOROBAN_FEE,
    networkPassphrase: NETWORK_PASSPHRASE,
  })
    .addOperation(transferOp)
    .setTimeout(60)
    .build();

  // Simulate to get ledger footprint + resource fee
  const simResult = await rpcServer.simulateTransaction(draftTx);

  if (rpc.Api.isSimulationError(simResult)) {
    throw new Error(`Simulation failed: ${simResult.error}`);
  }

  // assembleTransaction applies the footprint, resource fee, and auth entries
  const preparedTx = rpc
    .assembleTransaction(draftTx, simResult as rpc.Api.SimulateTransactionSuccessResponse)
    .build();

  return preparedTx.toXDR();
}

// ---------------------------------------------------------------------------
// Submit a signed Soroban TX via the RPC endpoint and poll for confirmation
// ---------------------------------------------------------------------------

const POLL_ATTEMPTS = 30;

async function submitSignedTx(signedXDR: string): Promise<string> {
  const server = new rpc.Server(HOOPS_RPC_URL);
  const tx = TransactionBuilder.fromXDR(signedXDR, NETWORK_PASSPHRASE);

  const sendResult = await server.sendTransaction(tx);

  if (sendResult.status === ("ERROR" as rpc.Api.SendTransactionStatus)) {
    const detail = sendResult.errorResult?.toXDR("base64") ?? "unknown";
    throw new Error(`Transaction rejected by network: ${detail}`);
  }

  const confirmed = await server.pollTransaction(sendResult.hash, {
    attempts: POLL_ATTEMPTS,
  });

  if (confirmed.status !== rpc.Api.GetTransactionStatus.SUCCESS) {
    throw new Error(`Transaction failed on-chain (status: ${String(confirmed.status)})`);
  }

  return sendResult.hash;
}

// ---------------------------------------------------------------------------
// WalletFund component
// ---------------------------------------------------------------------------

export function WalletFund({ smartAccountAddress, onSuccess }: WalletFundProps) {
  const { isConnected, address, balance, otherBalances, updateWalletInfo, signTransaction } =
    useWallet();

  // Balances as fetched from Horizon (may differ from WalletContext snapshot)
  const [localBalances, setLocalBalances] = useState<ClassicBalance | null>(null);
  const [isFetchingBalances, setIsFetchingBalances] = useState(false);
  const [balanceFetchError, setBalanceFetchError] = useState<string | null>(null);

  const [selectedAsset, setSelectedAsset] = useState<Asset>("XLM");
  const [rawAmount, setRawAmount] = useState("");

  const [phase, setPhase] = useState<TxPhase>("idle");
  const [txHash, setTxHash] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const busyRef = useRef(false);
  const amountInputId = useId();

  // ------------------------------------------------------------------
  // Connect wallet — mirrors ConnectWallet.tsx pattern.
  // The signer closure captures the kit instance so subsequent calls
  // to signTransaction route back through the same wallet session.
  // ------------------------------------------------------------------

  const handleConnect = useCallback(async () => {
    const kit = new StellarWalletsKit({
      network: WalletNetwork.TESTNET,
      selectedWalletId: XBULL_ID,
      modules: allowAllModules(),
    });

    try {
      await kit.openModal({
        onWalletSelected: (option) => {
          kit.setWallet(option.id);

          void (async () => {
            try {
              const { address: addr } = await kit.getAddress();

              const server = new Horizon.Server(HORIZON_URL, { allowHttp: false });
              const account: AccountResponse = await server.loadAccount(addr);

              const balances: BalanceLine[] = account.balances;
              let xlmBalance: BalanceLineNative | undefined;
              const otherBals: (
                | BalanceLineAsset<"credit_alphanum4" | "credit_alphanum12">
                | BalanceLineLiquidityPool
              )[] = [];

              for (const b of balances) {
                if (b.asset_type === "native") {
                  xlmBalance = b;
                } else {
                  otherBals.push(
                    b,
                  );
                }
              }

              const signer = buildSigner(kit);
              updateWalletInfo(
                true,
                addr,
                xlmBalance?.balance ?? "0",
                otherBals,
                signer,
              );

              // Fetch balances for local display
              setLocalBalances(null);
              setBalanceFetchError(null);
            } catch (err) {
              const msg =
                err instanceof Error ? err.message : "Failed to load account.";
              updateWalletInfo(true, null, "0", []);
              setBalanceFetchError(msg);
            }
          })();
        },
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Wallet connection failed.";
      console.error("[WalletFund] connect error:", err);
      setErrorMessage(msg);
    }
  }, [updateWalletInfo]);

  // ------------------------------------------------------------------
  // Refresh balances from Horizon
  // ------------------------------------------------------------------

  const handleRefreshBalances = useCallback(async () => {
    if (!address || isFetchingBalances) return;
    setIsFetchingBalances(true);
    setBalanceFetchError(null);

    try {
      const fetched = await fetchClassicBalances(address);
      setLocalBalances(fetched);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Could not load balances.";
      setBalanceFetchError(msg);
    } finally {
      setIsFetchingBalances(false);
    }
  }, [address, isFetchingBalances]);

  // ------------------------------------------------------------------
  // Send / transfer
  // ------------------------------------------------------------------

  const handleSend = useCallback(async () => {
    if (busyRef.current || !isConnected || !address || !signTransaction) return;

    const amountStroops = parseToStroops(rawAmount);
    if (amountStroops === null) return;

    busyRef.current = true;
    setPhase("building");
    setErrorMessage(null);
    setTxHash(null);

    try {
      // Pick the SAC address for the selected asset
      const sacAddress =
        selectedAsset === "XLM" ? TESTNET_TOKENS.XLM : TESTNET_TOKENS.USDC;

      // Build the Soroban invoke TX
      let txXDR: string;
      try {
        txXDR = await buildSacTransferTx(
          address,
          sacAddress,
          smartAccountAddress,
          amountStroops,
        );
      } catch (buildErr) {
        const msg =
          buildErr instanceof Error
            ? buildErr.message
            : "Failed to build transaction.";
        throw new Error(msg);
      }

      // Sign with the connected wallet
      setPhase("signing");
      let signedXDR: string;
      try {
        signedXDR = await signTransaction(txXDR, NETWORK_PASSPHRASE);
      } catch (signErr) {
        const msg =
          signErr instanceof Error
            ? signErr.message
            : "Wallet rejected the transaction.";
        throw new Error(msg);
      }

      // Submit to Horizon
      setPhase("submitting");
      let hash: string;
      try {
        hash = await submitSignedTx(signedXDR);
      } catch (submitErr) {
        const msg =
          submitErr instanceof Error
            ? submitErr.message
            : "Transaction submission failed.";
        throw new Error(msg);
      }

      setTxHash(hash);
      setPhase("success");
      setRawAmount("");

      // Refresh balances after success
      void fetchClassicBalances(address)
        .then((updated) => { setLocalBalances(updated); })
        .catch(() => { /* non-fatal */ });

      onSuccess?.();
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Transfer failed. Please try again.";
      setErrorMessage(msg);
      setPhase("error");
    } finally {
      busyRef.current = false;
    }
  }, [
    isConnected,
    address,
    signTransaction,
    rawAmount,
    selectedAsset,
    smartAccountAddress,
    onSuccess,
  ]);

  // ------------------------------------------------------------------
  // Derived state
  // ------------------------------------------------------------------

  const amountStroops = parseToStroops(rawAmount);
  const amountInvalid = rawAmount !== "" && amountStroops === null;

  // Displayed balances: prefer freshly fetched, fall back to WalletContext
  const displayXlm =
    localBalances?.xlm ?? balance ?? null;
  const displayUsdc =
    localBalances !== null
      ? localBalances.usdc
      : (otherBalances?.find(
          (b) =>
            b.asset_type !== "liquidity_pool_shares" &&
            (b).asset_code ===
              USDC_CODE,
        ) as BalanceLineAsset<"credit_alphanum4" | "credit_alphanum12"> | undefined)
          ?.balance ?? null;

  const selectedBalance =
    selectedAsset === "XLM" ? displayXlm : displayUsdc;

  // Max available — leave 1 XLM as minimum reserve when sending XLM
  function computeMax(): string {
    if (!selectedBalance) return "";
    const raw = parseFloat(selectedBalance);
    if (!isFinite(raw)) return "";
    const reserve = selectedAsset === "XLM" ? 1 : 0;
    const max = Math.max(0, raw - reserve);
    return max.toFixed(7).replace(/\.?0+$/, "");
  }

  function handleMax() {
    const m = computeMax();
    if (m) setRawAmount(m);
  }

  const isProcessing =
    phase === "building" || phase === "signing" || phase === "submitting";

  const canSend =
    isConnected &&
    !isProcessing &&
    !amountInvalid &&
    rawAmount !== "" &&
    amountStroops !== null &&
    amountStroops > 0n;

  // ------------------------------------------------------------------
  // Phase label for button
  // ------------------------------------------------------------------

  function actionLabel(): React.ReactNode {
    if (phase === "building")
      return (
        <>
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Building transaction...
        </>
      );
    if (phase === "signing")
      return (
        <>
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Waiting for wallet...
        </>
      );
    if (phase === "submitting")
      return (
        <>
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Submitting...
        </>
      );
    if (phase === "error")
      return (
        <>
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
          Retry Transfer
        </>
      );
    return (
      <>
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
        Send to Smart Account
      </>
    );
  }

  // ------------------------------------------------------------------
  // Render: not connected
  // ------------------------------------------------------------------

  if (!isConnected) {
    return (
      <section aria-label="Fund smart account from Stellar wallet">
        <div
          className="px-4 py-3.5 flex flex-col gap-3"
          style={{ borderLeft: "2px solid #1a3a25" }}
        >
          {/* Header row */}
          <div className="flex items-center gap-2.5">
            <Wallet
              className="h-3.5 w-3.5 flex-shrink-0"
              style={{ color: "#3a7a55" }}
              strokeWidth={1.5}
              aria-hidden="true"
            />
            <div className="min-w-0 flex items-baseline gap-2 flex-wrap">
              <p
                className="text-[12px] font-medium leading-none"
                style={{ color: "var(--text-primary)" }}
              >
                Fund from Stellar Wallet
              </p>
              <p
                className="text-[12px] leading-none"
                style={{ color: "var(--text-secondary)" }}
              >
                Transfer XLM or USDC from Freighter or xBull.
              </p>
            </div>
          </div>

          {/* Connect prompt */}
          <div className="flex items-center gap-3">
            <p className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
              Connect a classic Stellar wallet to continue.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="self-start px-3 h-7 text-[11px]"
              style={{
                background: "#141414",
                border: "1px solid #222",
                color: "#888",
              }}
              onClick={() => { void handleConnect(); }}
              aria-label="Connect a Stellar wallet to fund your smart account"
            >
              <Wallet className="h-3 w-3" aria-hidden="true" />
              Connect Wallet
            </Button>
          </div>

          {/* Error from connect attempt */}
          {errorMessage && (
            <p
              className="text-[12px] leading-relaxed"
              style={{ color: "#e05252" }}
              role="alert"
              aria-live="assertive"
            >
              {errorMessage}
            </p>
          )}
        </div>
      </section>
    );
  }

  // ------------------------------------------------------------------
  // Render: connected
  // ------------------------------------------------------------------

  return (
    <section aria-label="Fund smart account from Stellar wallet">
      <div
        className="px-4 py-3.5 flex flex-col gap-3"
        style={{ borderLeft: "2px solid #1a3a25" }}
      >
        {/* Header row */}
        <div className="flex items-center gap-2.5">
          <Wallet
            className="h-3.5 w-3.5 flex-shrink-0"
            style={{ color: "#3a7a55" }}
            strokeWidth={1.5}
            aria-hidden="true"
          />
          <div className="min-w-0 flex items-baseline gap-2 flex-wrap">
            <p
              className="text-[12px] font-medium leading-none"
              style={{ color: "var(--text-primary)" }}
            >
              Fund from Stellar Wallet
            </p>
            <p
              className="text-[12px] leading-none"
              style={{ color: "var(--text-secondary)" }}
            >
              Transfer XLM or USDC to your Hoops smart account.
            </p>
          </div>
        </div>

        {/* Connected wallet info row */}
        <div
          className="rounded-[var(--r)] px-3.5 py-3 flex items-center justify-between gap-3"
          style={{
            background: "var(--bg)",
            border: "1px solid var(--border-subtle)",
          }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="h-2 w-2 rounded-full flex-shrink-0"
              style={{ background: "var(--accent)" }}
              aria-hidden="true"
            />
            <span
              className="text-[12px] font-mono truncate"
              style={{ color: "var(--text-secondary)" }}
              title={address ?? ""}
            >
              {address ? truncateAddress(address) : "—"}
            </span>
          </div>

          <button
            type="button"
            onClick={() => { void handleRefreshBalances(); }}
            disabled={isFetchingBalances}
            className={cn(
              "flex items-center gap-1.5 text-[11px] transition-opacity hover:opacity-70",
              isFetchingBalances && "opacity-50 pointer-events-none",
            )}
            style={{ color: "var(--text-secondary)" }}
            aria-label="Refresh wallet balances"
          >
            <RefreshCw
              className={cn("h-3 w-3", isFetchingBalances && "animate-spin")}
              aria-hidden="true"
            />
            Refresh
          </button>
        </div>

        {/* Balance fetch error */}
        {balanceFetchError && (
          <p
            className="text-[12px] leading-relaxed -mt-2"
            style={{ color: "#e05252" }}
            role="alert"
          >
            {balanceFetchError}
          </p>
        )}

        {/* Asset selector */}
        <div className="flex flex-col gap-2">
          <p
            className="text-[10px] uppercase tracking-[0.08em] font-medium"
            style={{ color: "var(--text-secondary)" }}
          >
            Asset
          </p>

          <div
            className="grid grid-cols-2 rounded-[var(--r)] overflow-hidden"
            style={{ border: "1px solid var(--border-subtle)" }}
            role="group"
            aria-label="Select asset to transfer"
          >
            {(["XLM", "USDC"] as Asset[]).map((asset) => {
              const isActive = selectedAsset === asset;
              const bal = asset === "XLM" ? displayXlm : displayUsdc;

              return (
                <button
                  key={asset}
                  type="button"
                  onClick={() => {
                    setSelectedAsset(asset);
                    setRawAmount("");
                  }}
                  className={cn(
                    "px-4 py-3 flex flex-col gap-0.5 text-left transition-colors duration-150",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)]",
                    isActive
                      ? "bg-[var(--accent-dim)]"
                      : "bg-transparent hover:bg-[var(--surface-hi)]",
                  )}
                  aria-pressed={isActive}
                  aria-label={`Transfer ${asset}${bal ? `, balance: ${formatHorizonBalance(bal)} ${asset}` : ""}`}
                >
                  <span
                    className="text-[12px] font-semibold"
                    style={{
                      color: isActive ? "var(--accent)" : "var(--text-primary)",
                    }}
                  >
                    {asset}
                  </span>
                  <span
                    className="text-[11px] font-mono"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {bal !== null
                      ? `${formatHorizonBalance(bal)} available`
                      : asset === "USDC"
                      ? "No trustline"
                      : "—"}
                  </span>
                </button>
              );
            })}
          </div>

          {/* No USDC trustline notice */}
          {selectedAsset === "USDC" && displayUsdc === null && (
            <p
              className="text-[11px] leading-relaxed"
              style={{ color: "var(--text-secondary)" }}
            >
              USDC not found in this wallet. You may need to establish a trustline first.
            </p>
          )}
        </div>

        {/* Destination display */}
        <div className="flex flex-col gap-1.5">
          <p
            className="text-[10px] uppercase tracking-[0.08em] font-medium"
            style={{ color: "var(--text-secondary)" }}
          >
            Destination (Smart Account)
          </p>
          <div
            className="rounded-[var(--r)] px-3.5 py-2.5"
            style={{
              background: "var(--bg)",
              border: "1px solid var(--border-subtle)",
            }}
          >
            <span
              className="text-[12px] font-mono break-all"
              style={{ color: "var(--text-secondary)" }}
            >
              {smartAccountAddress}
            </span>
          </div>
        </div>

        {/* Amount input */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label
              htmlFor={amountInputId}
              className="text-[10px] uppercase tracking-[0.08em] font-medium"
              style={{ color: "var(--text-secondary)" }}
            >
              Amount
            </label>

            {selectedBalance && (
              <button
                type="button"
                onClick={handleMax}
                className="text-[11px] transition-opacity hover:opacity-70"
                style={{ color: "var(--accent)" }}
                aria-label={`Set max amount: ${computeMax()} ${selectedAsset}`}
                disabled={isProcessing}
              >
                Max
              </button>
            )}
          </div>

          <div className="relative">
            <Input
              id={amountInputId}
              type="number"
              min="0.0000001"
              step="any"
              placeholder="0.0000000"
              value={rawAmount}
              onChange={(e) => {
                setRawAmount(e.target.value);
                // Reset any prior result when user edits
                if (phase === "success" || phase === "error") {
                  setPhase("idle");
                  setErrorMessage(null);
                  setTxHash(null);
                }
              }}
              className="pr-16"
              aria-invalid={amountInvalid}
              aria-describedby={
                amountInvalid ? `${amountInputId}-error` : undefined
              }
              disabled={isProcessing}
            />
            <span
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold pointer-events-none"
              style={{ color: "var(--text-secondary)" }}
            >
              {selectedAsset}
            </span>
          </div>

          {amountInvalid && (
            <p
              id={`${amountInputId}-error`}
              className="text-[11px]"
              style={{ color: "#e05252" }}
              role="alert"
            >
              Enter a valid amount greater than 0
            </p>
          )}

          {/* Show human-readable stroops as confirmation */}
          {amountStroops !== null && amountStroops > 0n && (
            <p
              className="text-[11px]"
              style={{ color: "var(--text-secondary)" }}
              aria-live="polite"
            >
              {formatStroops(amountStroops)} {selectedAsset} ({amountStroops.toString()} stroops)
            </p>
          )}
        </div>

        {/* TX status */}
        {phase === "signing" && (
          <div
            className="flex items-center gap-2"
            aria-live="polite"
            role="status"
          >
            <Loader2
              className="h-4 w-4 animate-spin flex-shrink-0"
              style={{ color: "var(--accent)" }}
              aria-hidden="true"
            />
            <p className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
              Approve the transaction in your wallet...
            </p>
          </div>
        )}

        {phase === "submitting" && (
          <div
            className="flex items-center gap-2"
            aria-live="polite"
            role="status"
          >
            <Loader2
              className="h-4 w-4 animate-spin flex-shrink-0"
              style={{ color: "var(--accent)" }}
              aria-hidden="true"
            />
            <p className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
              Submitting to network...
            </p>
          </div>
        )}

        {phase === "success" && txHash && (
          <div
            className="rounded-[var(--r)] px-3.5 py-3 flex items-start gap-2.5"
            style={{
              background: "rgba(62, 207, 126, 0.07)",
              border: "1px solid rgba(62, 207, 126, 0.2)",
            }}
            aria-live="polite"
            role="status"
          >
            <CheckCircle2
              className="h-4 w-4 flex-shrink-0 mt-px"
              style={{ color: "var(--accent)" }}
              aria-hidden="true"
            />
            <div className="flex flex-col gap-0.5 min-w-0">
              <p className="text-[12px] font-medium" style={{ color: "var(--accent)" }}>
                Transfer confirmed
              </p>
              <a
                href={getTxExplorerUrl(txHash)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] transition-opacity hover:opacity-70 w-fit"
                style={{ color: "var(--text-secondary)" }}
                aria-label="View transaction on Stellar Explorer"
              >
                View transaction
                <ExternalLink className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
              </a>
            </div>
          </div>
        )}

        {phase === "error" && errorMessage && (
          <div
            className="rounded-[var(--r)] px-3.5 py-3 flex items-start gap-2.5"
            style={{
              background: "rgba(224, 82, 82, 0.07)",
              border: "1px solid rgba(224, 82, 82, 0.2)",
            }}
            role="alert"
            aria-live="assertive"
          >
            <AlertCircle
              className="h-4 w-4 flex-shrink-0 mt-px"
              style={{ color: "#e05252" }}
              aria-hidden="true"
            />
            <p className="text-[12px] leading-relaxed" style={{ color: "#e05252" }}>
              {errorMessage}
            </p>
          </div>
        )}

        {/* Submit button */}
        {phase !== "success" && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            style={{
              background: "#141414",
              border: "1px solid #222",
              color: "#888",
            }}
            disabled={!canSend}
            onClick={() => { void handleSend(); }}
            aria-label={
              canSend
                ? `Transfer ${rawAmount} ${selectedAsset} to smart account`
                : "Enter a valid amount to transfer"
            }
          >
            {actionLabel()}
          </Button>
        )}

        {/* Success — option to send again */}
        {phase === "success" && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            style={{
              background: "#141414",
              border: "1px solid #222",
              color: "#888",
            }}
            onClick={() => {
              setPhase("idle");
              setTxHash(null);
              setErrorMessage(null);
              setRawAmount("");
            }}
            aria-label="Fund smart account again"
          >
            Fund Again
          </Button>
        )}

        {/* Explainer footer */}
        <p
          className="text-[11px] leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          Transfers use the Stellar Asset Contract (SAC) to send tokens directly to
          your smart account address. Standard network fees apply.
        </p>
      </div>
    </section>
  );
}
